import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XCircle, Gift } from 'lucide-react';

const InviteCodeModal = ({ isOpen, onClose, onGenerate, loading }) => {
  const [formData, setFormData] = useState({
    userType: 'referral',
    maxUses: 1,
    expiresInDays: 30,
    description: '',
    campaign: ''
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userType) {
      newErrors.userType = 'User type is required';
    }

    if (!formData.maxUses || formData.maxUses < 1) {
      newErrors.maxUses = 'Max uses must be at least 1';
    }

    if (!formData.expiresInDays || formData.expiresInDays < 1) {
      newErrors.expiresInDays = 'Expiration must be at least 1 day';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onGenerate(formData);
    }
  };

  const handleClose = () => {
    // Reset form and errors
    setFormData({
      userType: 'referral',
      maxUses: 1,
      expiresInDays: 30,
      description: '',
      campaign: ''
    });
    setErrors({});
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg max-w-md w-full p-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-3">
              <Gift className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Generate Invite Code</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            disabled={loading}
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* User Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.userType}
              onChange={(e) => handleChange('userType', e.target.value)}
              className={`w-full border ${errors.userType ? 'border-red-500' : 'border-gray-300'} rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
              disabled={loading}
            >
              <option value="referral">Referral Agent</option>
              <option value="vendor">Vendor</option>
              <option value="customer">Customer</option>
            </select>
            {errors.userType && (
              <p className="mt-1 text-sm text-red-500">{errors.userType}</p>
            )}
          </div>

          {/* Max Uses */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Uses <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.maxUses}
              onChange={(e) => handleChange('maxUses', parseInt(e.target.value) || '')}
              className={`w-full border ${errors.maxUses ? 'border-red-500' : 'border-gray-300'} rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
              min="1"
              disabled={loading}
              placeholder="Enter maximum number of uses"
            />
            {errors.maxUses && (
              <p className="mt-1 text-sm text-red-500">{errors.maxUses}</p>
            )}
          </div>

          {/* Expires In Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires In (Days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.expiresInDays}
              onChange={(e) => handleChange('expiresInDays', parseInt(e.target.value) || '')}
              className={`w-full border ${errors.expiresInDays ? 'border-red-500' : 'border-gray-300'} rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
              min="1"
              disabled={loading}
              placeholder="Enter number of days until expiration"
            />
            {errors.expiresInDays && (
              <p className="mt-1 text-sm text-red-500">{errors.expiresInDays}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Optional description"
              disabled={loading}
            />
          </div>

          {/* Campaign */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign
            </label>
            <input
              type="text"
              value={formData.campaign}
              onChange={(e) => handleChange('campaign', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Optional campaign name"
              disabled={loading}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              'Generate Code'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default InviteCodeModal;
